import React, { useState, useEffect, useMemo } from 'react'
import { Grid, Box, Chip, MenuItem } from '@mui/material'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import { getDisplayTypeOptions } from 'services/content/display-types'
import { PRODUCT_TYPE_SEGMENTED } from 'services/content/product-types'
import { TIMEZONE_MOUNTAIN } from 'services/constants'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import MuiTextFieldWrapper from 'gui/components/common/MuiTextField'
import MuiSelectWrapper from 'gui/components/common/MuiSelect'
import MuiCheckbox from 'gui/components/common/MuiCheckbox'
import { TextareaWithCounter } from 'gui/components/textarea-with-counter'
import TextInputFormatSelect, {
  TEXT_FORMAT_RESTRICTED_HTML,
} from 'gui/components/text-input-format-select'
import { getLanguageLabel, getLanguageChipClass } from 'services/languages'
import { LANGUAGE_EN } from 'services/languages/constants'
import PRODUCTION_STATUS_OPTIONS from 'gui/components/production-status-select/options'
import Translations from './translations'
import type { ContentInfoChanges } from './types'
import './course-content-information-shared.scss'

interface ContentData {
  id: string
  contentId?: number
  contentType?: string
  title?: string
  language?: string
  path?: string
  published?: boolean
  publishedAtDate?: string
  contentSearchNoIndex?: boolean
  excludeFromRecommendations?: boolean
  productionStatus?: string
  generatePath?: boolean
  shortDescription?: string
  shortDescriptionFormat?: string
  description?: string
  descriptionFormat?: string
  thirdPartyDescription?: string
  createdAt?: string
  updatedAt?: string
  productType?: string
  displayType?: string
  director?: string
  producer?: string
  writer?: string
  studio?: string
  copyright?: string
  instructor?: string
  cast?: string
  primaryLanguage?: string
}

interface CourseContentInformationSectionProps {
  contentData: ContentData | null
  isViewOnly: boolean
  onDirtyChange: (isDirty: boolean) => void
  onChangesUpdate: (changes: ContentInfoChanges | null) => void
  onResetTriggered?: number
}

/**
 * Course Content Information Section Component
 * Displays and allows editing of course content metadata fields
 *
 * This component manages its own form state and reports dirty state and changes to parent
 */
function CourseContentInformationSection ({
  contentData,
  isViewOnly,
  onDirtyChange,
  onChangesUpdate,
  onResetTriggered,
}: CourseContentInformationSectionProps) {
  const id = contentData?.id
  const contentType = contentData?.contentType

  // Local state for form fields
  const [formValues, setFormValues] = useState({
    title: '',
    language: LANGUAGE_EN,
    path: '',
    published: false,
    publishedAtDate: '',
    contentSearchNoIndex: false,
    excludeFromRecommendations: false,
    productionStatus: '',
    generatePath: true,
    shortDescription: '',
    shortDescriptionFormat: TEXT_FORMAT_RESTRICTED_HTML,
    description: '',
    descriptionFormat: TEXT_FORMAT_RESTRICTED_HTML,
    thirdPartyDescription: '',
    createdAt: '',
    updatedAt: '',
    productType: '',
    displayType: '',
    director: '',
    producer: '',
    writer: '',
    studio: '',
    copyright: '',
    instructor: '',
    cast: '',
  })

  // Track initial values to detect changes
  const [initialValues, setInitialValues] = useState<Record<string, string | boolean>>({})

  // Update form values when content data loads
  useEffect(() => {
    if (contentData && Object.keys(contentData).length > 0) {
      const values = {
        title: contentData.title || '',
        language: contentData.language || LANGUAGE_EN,
        path: contentData.path || '',
        published: contentData.published || false,
        publishedAtDate: contentData.publishedAtDate || '',
        contentSearchNoIndex: contentData.contentSearchNoIndex || false,
        excludeFromRecommendations: contentData.excludeFromRecommendations || false,
        productionStatus: contentData.productionStatus || '',
        generatePath: contentData.generatePath ?? true,
        shortDescription: contentData.shortDescription || '',
        shortDescriptionFormat: contentData.shortDescriptionFormat || TEXT_FORMAT_RESTRICTED_HTML,
        description: contentData.description || '',
        descriptionFormat: contentData.descriptionFormat || TEXT_FORMAT_RESTRICTED_HTML,
        thirdPartyDescription: contentData.thirdPartyDescription || '',
        createdAt: contentData.createdAt || new Date().toISOString(),
        updatedAt: contentData.updatedAt || new Date().toISOString(),
        productType: contentData.productType || '',
        displayType: contentData.displayType || '',
        director: contentData.director || '',
        producer: contentData.producer || '',
        writer: contentData.writer || '',
        studio: contentData.studio || '',
        copyright: contentData.copyright || '',
        instructor: contentData.instructor || '',
        cast: contentData.cast || '',
      }
      setFormValues(values)
      setInitialValues(values)
    }
  }, [contentData])

  // Track if form has unsaved changes
  const isDirty = useMemo(() => {
    // If no initial values loaded yet, not dirty
    if (!initialValues || Object.keys(initialValues).length === 0) {
      return false
    }

    // Compare current form values with initial values
    return Object.keys(formValues).some((key) => {
      // Skip these fields from dirty check
      if (key === 'createdAt' || key === 'updatedAt') {
        return false
      }
      return formValues[key as keyof typeof formValues] !== initialValues[key]
    })
  }, [formValues, initialValues])

  // Notify parent when dirty state changes
  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  // Build changed values object
  const changedValues = useMemo(() => {
    if (!initialValues || Object.keys(initialValues).length === 0) {
      return null
    }

    const changes: Record<string, unknown> = { id }
    let hasChanges = false

    // Compare current values with initial values and only include changed fields
    Object.keys(formValues).forEach((field) => {
      // Skip fields we don't want to send to the mutation
      if (field === 'createdAt' || field === 'updatedAt' || field === 'generatePath') {
        return
      }

      // Check if field has changed
      const currentValue = formValues[field as keyof typeof formValues]
      const initialValue = initialValues[field]

      if (currentValue !== initialValue) {
        hasChanges = true
        // Convert empty strings to null for optional fields
        changes[field] = currentValue === '' ? null : currentValue
      }
    })

    return hasChanges
      ? ({
          contentInput: changes,
          generatePath: formValues.generatePath,
        } as ContentInfoChanges)
      : null
  }, [id, formValues, initialValues])

  // Notify parent when changed values update
  useEffect(() => {
    onChangesUpdate(changedValues)
  }, [changedValues, onChangesUpdate])

  // Handle reset triggered from parent
  useEffect(() => {
    if (onResetTriggered) {
      setFormValues({ ...initialValues } as typeof formValues)
    }
  }, [onResetTriggered, initialValues])

  // Handle field changes
  const handleFieldChange =
    (field: string) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | { target: { value: unknown; type?: string; checked?: boolean } },
    ) => {
      const { target } = event
      const value =
        target.type === 'checkbox' && 'checked' in target ? target.checked : target.value
      setFormValues((prev) => ({ ...prev, [field]: value }))
    }

  return (
    <SectionAccordion title="Content Information" collapsed={false}>
      <Grid container spacing={2} alignItems="stretch">
        {/* Left Column */}
        <Grid item xs={6}>
          <Box display="flex" flexDirection="column" height="100%">
            {/* Content ID and Type Header */}
            <div className="course-content-info__header">
              <p className="course-content-info__header-item">
                Content ID:{' '}
                <span className="course-content-info__header-label">{contentData?.contentId}</span>
              </p>
              <p className="course-content-info__header-item">
                Content Type:{' '}
                <span className="course-content-info__header-label">{contentType}</span>
              </p>
            </div>

            {/* Title Field */}
            <MuiTextFieldWrapper
              className="content-detail__input course-content-info__title-field"
              label="Title"
              name="title"
              type="text"
              value={formValues.title}
              onChange={handleFieldChange('title')}
              disabled={isViewOnly}
              fullWidth
              variant="outlined"
            />

            {/* Language Display */}
            <div className="course-content-info__language-row">
              <span className="course-content-info__language-label">Language:</span>
              <Chip
                label={getLanguageLabel(formValues.language)}
                size="small"
                variant="outlined"
                className={`language-chip ${getLanguageChipClass(formValues.language)} course-content-info__language-chip`}
              />
              {contentData?.primaryLanguage && (
                <>
                  <span className="course-content-info__primary-language-label">
                    Primary Language
                  </span>
                  <Chip
                    label={getLanguageLabel(contentData.primaryLanguage)}
                    size="small"
                    variant="outlined"
                    className={`language-chip ${getLanguageChipClass(contentData.primaryLanguage)} course-content-info__language-chip`}
                  />
                </>
              )}
            </div>

            {/* Path Field */}
            <MuiTextFieldWrapper
              className="content-detail__input course-content-info__path-field"
              label="Path"
              name="path"
              type="text"
              value={formValues.path}
              disabled={isViewOnly || formValues.generatePath}
              onChange={handleFieldChange('path')}
              fullWidth
              variant="outlined"
            />

            {/* Generate Path Checkbox */}
            <MuiCheckbox
              label="Generate path for me (only applies to source language)"
              name="generatePath"
              onChange={handleFieldChange('generatePath')}
              checked={formValues.generatePath}
              disabled={isViewOnly}
            />
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={6}>
          <Box display="flex" flexDirection="column" height="100%">
            {/* Publish On Date and Published Chip Row */}
            <div className="course-content-info__publish-row">
              <div className="course-content-info__publish-date-wrapper">
                <MuiTextFieldWrapper
                  className="content-detail__input course-content-info__publish-date-field"
                  label="Publish On Date"
                  name="publishedAtDate"
                  type="datetime-local"
                  value={
                    Number.isNaN(Date.parse(formValues.publishedAtDate))
                      ? ''
                      : formatInTimeZone(
                          formValues.publishedAtDate,
                          TIMEZONE_MOUNTAIN,
                          "yyyy-MM-dd'T'HH:mm",
                        )
                  }
                  onChange={(e) => {
                    const { value } = e.target
                    const utcValue = Number.isNaN(Date.parse(value))
                      ? ''
                      : zonedTimeToUtc(value, TIMEZONE_MOUNTAIN).toISOString()
                    setFormValues((prev) => ({ ...prev, publishedAtDate: utcValue }))
                  }}
                  disabled={isViewOnly}
                  fullWidth
                  variant="outlined"
                />
                <div className="course-content-info__publish-date-hint">
                  (Date/Times are displayed in Mountain Time)
                </div>
              </div>
              <Chip
                label={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 15,
                    }}
                  >
                    <MuiCheckbox
                      label=""
                      name="published"
                      onChange={handleFieldChange('published')}
                      checked={formValues.published}
                      disabled={isViewOnly}
                      sx={{
                        padding: 0,
                        marginTop: '6px',
                        '& .MuiFormControlLabel-root': {
                          margin: 0,
                        },
                        '& .MuiCheckbox-root': {
                          padding: 0,
                          color: 'white',
                          '&.Mui-checked': {
                            color: 'white',
                          },
                        },
                      }}
                    />
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        marginLeft: -7,
                        marginBottom: '-2px',
                      }}
                    >
                      Published
                    </span>
                  </div>
                }
                sx={{
                  background: formValues.published
                    ? 'linear-gradient(135deg, #81c784 0%, #a5d6a7 100%)'
                    : '#e57373',
                  color: 'white',
                  borderRadius: '20px',
                  height: '56px',
                  alignSelf: 'flex-start',
                  marginTop: '0px',
                  cursor: isViewOnly ? 'default' : 'pointer',
                  '& .MuiChip-label': {
                    padding: '8px 16px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '&:hover': {
                    background: (() => {
                      if (isViewOnly) {
                        return formValues.published
                          ? 'linear-gradient(135deg, #81c784 0%, #a5d6a7 100%)'
                          : '#e57373'
                      }
                      return formValues.published
                        ? 'linear-gradient(135deg, #66bb6a 0%, #81c784 100%)'
                        : '#da190b'
                    })(),
                  },
                }}
                variant="filled"
                onClick={
                  isViewOnly
                    ? undefined
                    : () => {
                        handleFieldChange('published')({
                          target: {
                            value: !formValues.published,
                            type: 'checkbox',
                            checked: !formValues.published,
                          },
                        })
                      }
                }
              />
            </div>

            {/* Hide from Search and Hide from Recommendations */}
            <div className="course-content-info__checkboxes">
              <MuiCheckbox
                label="Hide from Search"
                name="contentSearchNoIndex"
                onChange={handleFieldChange('contentSearchNoIndex')}
                checked={formValues.contentSearchNoIndex}
                disabled={isViewOnly}
                className="course-content-info__checkbox-first"
              />
              <MuiCheckbox
                label="Hide from Recommendations"
                name="excludeFromRecommendations"
                onChange={handleFieldChange('excludeFromRecommendations')}
                checked={formValues.excludeFromRecommendations}
                disabled={isViewOnly}
              />
            </div>
          </Box>
        </Grid>
      </Grid>

      {/* Translations Row */}
      {contentData?.contentId && contentType && (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12}>
            <Translations
              contentId={contentData.contentId}
              contentType={contentType}
              currentLanguage={formValues.language}
              primaryLanguage={contentData?.primaryLanguage}
              isViewOnly={isViewOnly}
            />
          </Grid>
        </Grid>
      )}

      {/* Short Description */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <TextareaWithCounter
            className="content-detail__input"
            label="Short description"
            name="shortDescription"
            value={formValues.shortDescription}
            onChange={handleFieldChange('shortDescription')}
            onBlur={() => {}}
            disabled={isViewOnly}
            error={false}
            helperText=""
            maxValue={150}
          />
          <TextInputFormatSelect
            className="content-detail__input"
            label="Short description format"
            value={formValues.shortDescriptionFormat}
            name="shortDescriptionFormat"
            onChange={handleFieldChange('shortDescriptionFormat')}
            disabled={isViewOnly}
          />
        </Grid>
      </Grid>

      {/* Long Description */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <TextareaWithCounter
            className="content-detail__input"
            label="Long description"
            name="description"
            value={formValues.description}
            onChange={handleFieldChange('description')}
            onBlur={() => {}}
            disabled={isViewOnly}
            error={false}
            helperText=""
            maxValue={4000}
            rows={10}
          />
          <TextInputFormatSelect
            className="content-detail__input"
            label="Long description format"
            name="descriptionFormat"
            value={formValues.descriptionFormat}
            onChange={handleFieldChange('descriptionFormat')}
            disabled={isViewOnly}
          />
        </Grid>
      </Grid>

      {/* Third Party Description */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <TextareaWithCounter
            className="content-detail__input"
            label="Third Party Description"
            name="thirdPartyDescription"
            value={formValues.thirdPartyDescription}
            onChange={handleFieldChange('thirdPartyDescription')}
            onBlur={() => {}}
            disabled={isViewOnly}
            error={false}
            helperText=""
            maxValue={255}
          />
        </Grid>
      </Grid>

      {/* Created/Updated Dates - displayed in Mountain Time to match legacy content detail */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Created at"
            name="createdAt"
            type="text"
            value={
              formValues.createdAt
                ? formatInTimeZone(formValues.createdAt, TIMEZONE_MOUNTAIN, 'yyyy-MM-dd')
                : ''
            }
            disabled={true}
            fullWidth
            variant="outlined"
          />
        </Grid>
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Updated at"
            name="updatedAt"
            type="text"
            value={
              formValues.updatedAt
                ? formatInTimeZone(formValues.updatedAt, TIMEZONE_MOUNTAIN, 'yyyy-MM-dd')
                : ''
            }
            disabled={true}
            fullWidth
            variant="outlined"
          />
        </Grid>
      </Grid>

      {/* Product Type and Display Type */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <MuiSelectWrapper
            className="content-detail__input"
            id="productType"
            name="productType"
            label="Product type"
            value={formValues.productType || ''}
            onChange={handleFieldChange('productType')}
            required={true}
            disabled={isViewOnly}
          >
            <MenuItem value="">Please select a product type</MenuItem>
            <MenuItem value={PRODUCT_TYPE_SEGMENTED}>Segmented</MenuItem>
          </MuiSelectWrapper>
        </Grid>
        <Grid item xs={6}>
          <MuiSelectWrapper
            className="content-detail__input"
            name="displayType"
            id="displayType"
            label="Display type"
            value={formValues.displayType || ''}
            onChange={handleFieldChange('displayType')}
            required={true}
            disabled={isViewOnly}
          >
            {getDisplayTypeOptions(contentType)
              .map((option) => (
                <MenuItem value={option?.get('value')} key={`display-type-${option?.get('value')}`}>
                  {option?.get('label')}
                </MenuItem>
              ))
              .toArray()}
          </MuiSelectWrapper>
        </Grid>
      </Grid>

      {/* Production Status */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <MuiSelectWrapper
            className="content-detail__input"
            id="productionStatus"
            name="productionStatus"
            label="Production status"
            value={formValues.productionStatus || ''}
            onChange={handleFieldChange('productionStatus')}
            disabled={isViewOnly}
          >
            {PRODUCTION_STATUS_OPTIONS.map((option) => {
              if (!option) return null
              return (
                <MenuItem
                  value={option.get('value')}
                  key={`production-status-${option.get('value')}`}
                >
                  {option.get('label')}
                </MenuItem>
              )
            }).toArray()}
          </MuiSelectWrapper>
        </Grid>
      </Grid>

      {/* Director and Producer */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Director"
            name="director"
            type="text"
            value={formValues.director || ''}
            onChange={handleFieldChange('director')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Producer"
            name="producer"
            type="text"
            value={formValues.producer || ''}
            onChange={handleFieldChange('producer')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
      </Grid>

      {/* Writer and Studio */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Writer"
            name="writer"
            type="text"
            value={formValues.writer || ''}
            onChange={handleFieldChange('writer')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Studio"
            name="studio"
            type="text"
            value={formValues.studio || ''}
            onChange={handleFieldChange('studio')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
      </Grid>

      {/* Copyright and Instructor */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Copyright"
            name="copyright"
            type="text"
            value={formValues.copyright || ''}
            onChange={handleFieldChange('copyright')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
        <Grid item xs={6}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Instructor"
            name="instructor"
            type="text"
            value={formValues.instructor || ''}
            onChange={handleFieldChange('instructor')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
      </Grid>

      {/* Cast */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <MuiTextFieldWrapper
            className="content-detail__input"
            label="Cast (separated by commas)"
            name="cast"
            type="text"
            value={formValues.cast || ''}
            onChange={handleFieldChange('cast')}
            disabled={isViewOnly}
            fullWidth
            variant="outlined"
          />
        </Grid>
      </Grid>
    </SectionAccordion>
  )
}

export default CourseContentInformationSection
